import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import TableComponent from "../../TableComponent/TableComponent";
import IconButton from "../../IconButton/IconButton";
import ModalComponent from "../../ModalComponent/ModalComponent";
import PlusIcon from "../../../../assets/PlusIcon";
import "./LevelManagement.css";
import { getAllLevels, createNewLevel, updateLevel } from "../../../../services/svt/svt-admin/level-management.service";
import Loader from "../../../common/Loader/Loader";
import validateFields from '../../../../utils/ValidationInput';
import { toast } from 'react-toastify';
import { DataTableComponent } from "../../../common/TableComponent/DataTableComponent";
import { formatDate } from "../../../../utils/dateFormat";
 
const LevelManagement = () => {
    const [loading, setLoading] = useState(true);
    const { t, i18n } = useTranslation();
 
    const columns = [
        { name: t("levelName"), field: "name", selector: row => row.name, sortable: true },
        { name: t("levelCutOff"), field: "cutOff", selector: row => row.cutOff, sortable: true },
        {
          name: t("modifiedDate"), field: "modifiedDate", selector: row => row.modifiedDate, sortable: true
        },
        { name: t("levelWeightage"), field: "weightage", selector: row => row.weightage, sortable: true },
    ];
 
    const options = [
        { value: "active", label: "Active" },
        { value: "deactive", label: "In-active" },
    ];
 
    const [isModalOpen, setIsModalOpen] = useState(false);
    // const [levelData, setLevelData] = useState(newLevelData);
    const [statusList, setStatusList] = useState(options);
    const [tableData, setTableData] = useState([]);
    const [modalType, setModalType] = useState('');
    const [selectedRowIndex, setSelectedRowIndex] = useState('');
    const [formData, setFormData] = useState({
        nameEn: '',
        nameJa: '',
        nameZh: '',
        cutOff: '',
        status: 'active',
        modifiedDate: "",
        weightage: ''
    });
 
    const getLevelData = () => {
        // Show only the input for the current language when editing
        if (modalType === 'edit') {
            let langField = null;
            if (i18n.language === 'en') {
                langField = [{ label: '(En)', name: "nameEn", fieldValue: formData.nameEn || '' }];
            } else if (i18n.language === 'jp' || i18n.language === 'ja') {
                langField = [{ label: '(Jp)', name: "nameJa", fieldValue: formData.nameJa || '' }];
            } else if (i18n.language === 'ch' || i18n.language === 'zh') {
                langField = [{ label: '(Ch)', name: "nameZh", fieldValue: formData.nameZh || '' }];
            } else {
                // fallback to all
                langField = [
                    { label: '(En)', name: "nameEn", fieldValue: formData.nameEn || '' },
                    { label: '(Jp)', name: "nameJa", fieldValue: formData.nameJa || '' },
                    { label: '(Ch)', name: "nameZh", fieldValue: formData.nameZh || '' }
                ];
            }
            return [
                {
                    fieldName: t('levelName'),
                    type: 'multiInput',
                    fields: langField
                },
                { fieldName: t('levelCutOff'), name: "cutOff", fieldValue: formData.cutOff || '', type: 'input', inputType: 'number' },
                { fieldName: t('levelWeightage'), name: "weightage", fieldValue: formData.weightage || '', type: 'input', inputType: 'number' }
            ];
        }
        // Otherwise, show all language inputs
        return [
            {
                fieldName: t('levelName'),
                type: 'multiInput',
                fields: [
                    { label: '(En)', name: "nameEn", fieldValue: formData.nameEn || '' },
                    { label: '(Jp)', name: "nameJa", fieldValue: formData.nameJa || '' },
                    { label: '(Ch)', name: "nameZh", fieldValue: formData.nameZh || '' }
                ]
            },
            { fieldName: t('levelCutOff'), name: "cutOff", fieldValue: formData.cutOff || '', type: 'input', inputType: 'number' },
            { fieldName: t('levelWeightage'), name: "weightage", fieldValue: formData.weightage || '', type: 'input', inputType: 'number' }
        ];
    };
 
    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        fetchData();
    }, [i18n.language]);
 
    // Fetch all levels
    const fetchData = async () => {
        try {
            setLoading(true);
            const levelsResult = await getAllLevels();
                const updatedData = levelsResult.data.map(item => ({
                    ...item,
                    name:
                    item.name === 'Basic' ? t('basic') :
                    item.name === 'Developing' ? t('developing') :
                    item.name === 'Mature' ? t('mature') :
                    item.name,
                    status: item.isActive === true ? 'Active' : 'In-Active',
            }));
            setTableData(updatedData);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };
 
    const closeModal = () => {
        setIsModalOpen(false);
        setFormData({
            nameEn: '',
            nameJa: '',
            nameZh: '',
            cutOff: '',
            status: 'active',
            modifiedDate: "",
            weightage: ''
        })
    }
 
    const onAddNewLevel = () => {
        setIsModalOpen(true);
        setModalType('add');
    };
 
    const onChangeInput = (inputData) => {
        const { name, value } = inputData;
        toast.dismiss();
        if ((name === "nameEn" || name === "nameJa" || name === "nameZh") && value.length > 30) {
            toast.error(t("levelNameExceed"), { position: "top-center", theme: "dark" });
            return;
        }
        setFormData((prevData) => ({
            ...prevData,
            [name]: value
        }));
    };
 
    const trimText = (text) => {
        return text?.trim().replace(/\s+/g, ' ');
    };
 
    const saveData = async () => {
        setLoading(true);
        // Required fields with types (text or email)
        const requiredFields = [
            { label: t("levelName"), key: "Level Name (En)", type: "text", value: trimText(formData.nameEn)},
            { label: t("levelName"), key: "Level Name (Jp)", type: "text", value: trimText(formData.nameJa)},
            { label: t("levelName"), key: "Level Name (Ch)", type: "text", value: trimText(formData.nameZh)},
            { label: t("levelCutOff"), key: "Level Cut Off", type: "number", value: formData.cutOff},
            { label: t("levelWeightage"), key: "Level Weightage", type: "number", value: formData.weightage},
        ];
        const isValid = validateFields(formData, requiredFields, 'byValue');
        if (!isValid) {
            setLoading(false);
            return;
        } else {
            if (formData.cutOff > 100) {
                toast.error(t("levelCutOffExceed"), { position: "top-center", theme: "dark" });
                setLoading(false);
                return;
            }
        }
 
        let payload = {
            name: trimText(formData.nameEn),
            name_jp: trimText(formData.nameJa),
            name_ch: trimText(formData.nameZh),
            cutOff: parseInt(formData.cutOff),
            weightage: formData.weightage,
            isActive: formData.status === 'active' ? true : false
        }
        await createNewLevel(payload).then((response) => {
            if (response === null) {
                setLoading(false);
            } else {
                fetchData();
                closeModal();
                setLoading(false);
            }
        })
    };
 
    const updateData = async () => {
        setLoading(true);
        // Required fields with types (text or email)
        const requiredFields = [
            { key: "Level Name", type: "text", value: trimText(formData.name || formData.nameJa || formData.nameZh), label: t('levelName') },
            { key: "Level Cut Off", type: "number", value: formData.cutOff === 0 ? "0" : formData.cutOff, label: t('levelCutOff') },
            { key: "Level Weightage", type: "number", value: formData.weightage, label: t('levelWeightage') },
        ];
        const isValid = validateFields(formData, requiredFields, 'byValue');
        if (!isValid) {
            setLoading(false);
            return;
        } else {
            if (formData.cutOff > 100) {
                toast.error(t("levelCutOffExceed"), { position: "top-center", theme: "dark" });
                setLoading(false);
                return;
            }
        }
 
        let payload = {
            name: trimText(formData.name),
            name_jp: trimText(formData.nameJa),
            name_ch: trimText(formData.nameZh),
            cutOff: formData.cutOff,
            weightage: formData.weightage,
            isActive: formData.status === 'active' ? true : false,
            id: formData.id
        }
        await updateLevel(payload).then((response) => {
            if (response === null) {
                setLoading(false);
            } else {
                fetchData();
                closeModal();
                setLoading(false);
            }
        })
    };
 
    const onEditDetails = (data, selectedIndex) => {
        setModalType('edit');
        setIsModalOpen(true);
        setSelectedRowIndex(selectedIndex);
        let { nameEn, nameJa, nameZh, cutOff, status, modifiedDate, weightage, id, isActive, name } = data;
        setFormData({
            nameEn: name,
            nameJa: name,
            nameZh: name,
            cutOff: cutOff,
            status: status === 'Active' ? 'active' : 'deactive',
            modifiedDate: modifiedDate,
            weightage: weightage,
            id: id
        })
    };
    return (
        <>
            {loading ? <Loader /> :
                <div className={["glbOuterWrapper", "levelMgmt"].join(' ')}>
                    {/* <div className="buttonOuterDiv">
                        <IconButton
                            text="New Level"
                            onClick={onAddNewLevel}
                            style={{ backgroundColor: "#F0F0F0", color: '#454647', border: '1px solid #B3B2B4' }}
                            icon={<PlusIcon />}
                        />
                    </div> */}
                    <div className="surveyHeader">
                        <h2 className="surveyTitle">{t("listOfLevels")}</h2>
                        {/* <IconButton
                            text={t("newLevel")}
                            onClick={onAddNewLevel}
                            style={{
                                backgroundColor: "#F0F0F0",
                                color: "#454647",
                                border: "1px solid #B3B2B4",
                            }}
                            icon={<PlusIcon />}
                        /> */}
                    </div>
                    <hr></hr>
 
                    <div style={{ marginTop: "1rem" }}>
                        <DataTableComponent
                            columns={columns}
                            data={tableData}
                            onEditDetails={onEditDetails}
                            tableName="level"
                            noDataMessage={t('noDataFound')}
                            pageCount={5}
                        />
                    </div>
 
                    <ModalComponent
                        modalTitle={modalType === 'add' ? t("addNewLevel") : t("updateLevel")}
                        isOpen={isModalOpen}
                        fieldsData={getLevelData()}
                        dropdownOptions={statusList}
                        formData={formData}
                        modalType={modalType}
                        onClose={closeModal}
                        onChangeInput={onChangeInput}
                        modalName={'level'}
                        buttons={[
                            <button className="saveButton levelAddBtn" onClick={modalType === 'add' ? saveData : updateData}
                                disabled={tableData.length > 3 && modalType === 'add' ? true : false}
                            >
                                {modalType === 'add' ? t("add") : t("update")}
                            </button>,
                            <button className="cancelButton" onClick={closeModal}>{t("cancel")}</button>
                        ]}
                    />
                </div>
            }
        </>
    );
}
 
export default LevelManagement;